import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from '@js/modules/axios/index';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import {
  CorporateProfile,
  AddressType,
  RegistrationFormData,
} from '@js/interfaces/index';
import { CorporateRegistrationValidation } from '@js/formSettings/validations';
import { Address } from '@js/components/common/AddressRegistration';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import { InvestorRegistrationContainer } from '@js/components/common/investorRegistrations/InvestorRegistrationContainer';
import { CONFIRM_MESSAGE_IDENTIFICATION_NEEDED } from '@js/components/corporateRegistrations/constants';
import Toast from '@js/modules/toast';
import { SingleInputUnit } from '@js/components/FormUnits/SingleInputUnit';
import { InputField } from '../FormUnits/InputField';

export const CorporateRegistration = () => {
  const { setIsLoading, isEdit, setIsEdit } = useContext(Context);
  const [corporate_profile, set_corporate_profile] = useState<CorporateProfile>(
    {
      name: '',
      name_kana: '',
      corporate_number: '',
      phone_number: '',
      business_overview: '',
    }
  );
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [uploadedFileErrorText, setUploadedFileErrorText] = useState('');
  const [address, set_address] = useState<AddressType>({
    postal_code: '',
    prefecture_name: '',
    city: '',
    street: '',
    building: '',
  });

  const methods = useForm<RegistrationFormData>({
    shouldUnregister: false,
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
  } = methods;

  const watchPostalCode = watch('postal_code');
  const { currentStep, pushNext } = usePath(isEdit);
  const onSubmit = (data: RegistrationFormData) => {
    const {
      name,
      name_kana,
      corporate_number,
      phone_number,
      business_overview,
      postal_code,
      prefecture_name,
      city,
      street,
      building,
    } = data;
    
    if (isEdit && !confirm(CONFIRM_MESSAGE_IDENTIFICATION_NEEDED('基本情報')))
      return;

    const formData = new FormData();
    
    if (data.pdf != null) {
      formData.append(
        'corporate_registration_document[pdf]',
        data.pdf[0],
        data.pdf[0].name
      );
    }

    formData.append('corporate_profile[name]', name);
    formData.append('corporate_profile[name_kana]', name_kana);
    formData.append('corporate_profile[corporate_number]', corporate_number);
    formData.append('corporate_profile[phone_number]', phone_number);
    formData.append(
      'corporate_profile[business_overview]',
      business_overview
    );

    formData.append('address[postal_code]', postal_code);
    formData.append('address[prefecture_name]', prefecture_name);
    formData.append('address[city]', city);
    formData.append('address[street]', street);
    formData.append('address[building]', building);

    axios
      .post('/api/v1/customer/corporate_registration', formData)
      .then((response) => {
        if (isEdit) {
          const message = response.data.message;
          Toast.setFlashNotice(message);
        }
        pushNext();
      })
      .catch((error) => {
        const errorText = error.response.data.errors[0];
        if (errorText.match(/履歴事項全部証明書/)) {
          setUploadedFileErrorText(errorText);
        }
      });
  };

  useEffect(() => {
    setIsLoading(true);
    axios.get('/api/v1/customer/corporate_registration').then((response) => {
      const data = response.data;
      set_corporate_profile(data.corporate_profile);
      set_address(data.address);
      if (data.corporate_document.pdf.metadata) {
        setUploadedFileName(data.corporate_document.pdf.metadata.filename);
      }
      setIsEdit(!data['registration_in_progress?']);
    });
  }, []); // eslint-disable-line

  const setFormValues = useCallback(
    (data: Omit<RegistrationFormData, 'pdf'>) => {
      const keys = Object.keys(data) as (keyof Omit<
        RegistrationFormData,
        'pdf'
      >)[];
      keys.forEach((key) => {
        if (data[key]) setValue(key, data[key]);
      });
    },
    [setValue]
  );
  useEffect(() => {
    setFormValues({ ...corporate_profile, ...address });
    setIsLoading(false);
  }, [corporate_profile, address, setValue, setIsLoading, setFormValues]);

  return (
    <InvestorRegistrationContainer formId={currentStep.name} isEdit={isEdit}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="form-wrap mt40"
          id={currentStep.name}
        >
          <InputField name="name" label="法人名">
            <SingleInputUnit
              name="name"
              required={true}
              label="法人名"
              inputType="text"
              placeholder="例）株式会社xx"
              validation={CorporateRegistrationValidation.name}
              initialValue={corporate_profile.name}
            />
          </InputField>
          <InputField name="name_kana" label="法人名（カタカナ）">
            <SingleInputUnit
              name="name_kana"
              required={true}
              label="法人名（カタカナ）"
              inputType="text"
              placeholder="例）カブシキガイシャバツバツ"
              validation={CorporateRegistrationValidation.nameKana}
              initialValue={corporate_profile.name_kana}
            />
          </InputField>
          <InputField name="corporate_number" label="法人番号">
            <SingleInputUnit
              name="corporate_number"
              required={true}
              label="法人番号"
              inputType="number"
              placeholder="例）1234567891011"
              validation={CorporateRegistrationValidation.corporateNumber}
              initialValue={corporate_profile.corporate_number}
            />
          </InputField>
          <InputField
            name="phone_number"
            label="代表電話番号"
            noteMessage="※ハイフン無しでご入力ください"
          >
            <SingleInputUnit
              name="phone_number"
              required={true}
              label="代表電話番号"
              inputType="tel"
              placeholder="例）0398765432"
              validation={CorporateRegistrationValidation.phoneNumber}
              initialValue={corporate_profile.phone_number}
            />
          </InputField>
          <div className="form-input__wrap">
            <label className="form-label" htmlFor="business_overview">
              <span className="form-label__required">必須</span>事業内容
            </label>
            <textarea
              {...register(
                'business_overview',
                CorporateRegistrationValidation.businessOverview
              )}
              className={`form-input form-input-textarea ${
                errors.business_overview && 'is-error'
              }`}
              placeholder="例）不動産業"
              name="business_overview"
              id="business_overview"
            />
            <ErrorMessage
              errors={errors}
              name="business_overview"
              render={({ message }) => (
                <span className="form-input__error">事業内容は{message}</span>
              )}
            />
          </div>
          <Controller
            control={control}
            name="pdf"
            rules={{
              validate: {
                isUploaded: (value) =>
                  Boolean(value && value[0]) ||
                  Boolean(uploadedFileName) ||
                  '必ずアップロードしてください',
              },
            }}
            render={({ field: { onChange, value } }) => (
              <div className="form-input__wrap">
                <label className="form-label" htmlFor="pdf">
                  <span className="form-label__required">必須</span>
                  履歴事項全部証明書(PDF)
                </label>
                <div className="pb8 mt16">
                  <div className="form-file__wrap">
                    <span className="button-short-base--line form-file__button">
                      <input
                        onChange={(e) => {
                          if (
                            e &&
                            e.target &&
                            e.target.files &&
                            e.target.files.length > 0
                          )
                            onChange(e.target.files);
                        }}
                        className={`form-file form-file--corporate ${
                          errors.pdf && 'is-error'
                        }`}
                        type="file"
                        accept=".pdf"
                        name="pdf"
                        id="pdf"
                      />
                      ファイルを選択する
                    </span>
                    <div className="form-file__name">
                      {(value && value[0].name) ||
                        uploadedFileName ||
                        '選択されていません'}
                    </div>
                  </div>
                  <div className="form-input__error">
                    {uploadedFileErrorText}
                  </div>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="pdf"
                  render={({ message }) => (
                    <span className="form-input__error">
                      履歴事項全部証明書(PDF)は{message}
                    </span>
                  )}
                />
                <ul className="list-short-notes">
                  <li className="list-short-notes__item">
                    ※pdfファイルのみアップロード可能です
                  </li>
                  <li className="list-short-notes__item">
                    ※ファイルサイズは5MB以下にしてください
                  </li>
                </ul>
              </div>
            )}
          />
          <Address
            register={register}
            setValue={setValue}
            watchPostalCode={watchPostalCode}
            errors={errors}
          />
        </form>
      </FormProvider>
    </InvestorRegistrationContainer>
  );
};
